/**
 * Supervision BOOKINGS — a calendar event is not supervision completion.
 *
 * ===========================================================================
 * WHY THIS IS A MODULE OF ITS OWN AND NOT FOUR MORE FUNCTIONS IN `supervision`
 * ===========================================================================
 * A booking is an INTENTION. A completed supervision session is an OBSERVED
 * FACT with a compliance consequence. `supervision.ts` is the module of things
 * that have happened — `recordSession`, `correctSession`, the compliance
 * verdicts computed from them. Putting `book()` beside `recordSession()` in one
 * namespace invites the call site to read them as two spellings of the same
 * act, and the whole defect this capability is named for is exactly that
 * reading. `supervisionBookings.book(...)` cannot be mistaken for a record;
 * `supervision.book(...)` can.
 *
 * The types stay where they are. A booking and a session are ONE ROW in
 * `supervision_session` — a booking is a row in status `scheduled`, and
 * confirming it is an UPDATE, not a second insert — so re-declaring
 * `SessionView` here would create two client types for one table, which is a
 * worse error than the one the split avoids. This module imports it.
 *
 * ===========================================================================
 * THE FOUR ROUTES AND THE ONE THAT IS A DECISION
 * ===========================================================================
 *   book      -> 'scheduled'   counts for NOTHING
 *   occurred  -> 'occurred'    counts for NOTHING
 *   confirm   -> 'recorded'    the obligation is satisfied
 *   close     -> 'cancelled' | 'no_show'   the obligation stays OPEN
 *
 * All four sit behind `supervision:record` — the SAME key the direct-record
 * door charges, and this module proposes no new one. That is not laziness: the
 * route file states the reason ("being able to put supervision on somebody's
 * compliance record is one authority regardless of which door it comes
 * through"), and a separate `supervision:book` key would let an organization
 * grant the door that creates the row while withholding the door that files
 * one, which is a distinction the server would refuse to honour anyway.
 *
 * The narrowing that matters is not a key. `assertMayConfirm` refuses everybody
 * except the supervisor of record — INCLUDING organization-wide management —
 * because confirming is attesting and a manager who was not in the room cannot
 * attest. A surface therefore cannot decide from a permission alone whether to
 * offer Confirm; it has to know who the supervisor of record is. See
 * {@link mayConfirm}.
 */
import { post } from './transport.js';
/** `POST /supervision/bookings` — creates a row in status `scheduled`. */
export async function book(input, options) {
    const body = await post('/supervision/bookings', input, options);
    return body.session;
}
/**
 * `POST /supervision/bookings/:id/occurred` — "the meeting took place".
 *
 * STILL NOT COMPLIANCE, and this binding exists to make that state reachable
 * rather than skippable. Somebody observed the slot was used; the supervisor
 * has not yet attested that supervision was delivered.
 */
export async function markOccurred(id, input, options) {
    const body = await post(`/supervision/bookings/${id}/occurred`, input, options);
    return body.session;
}
/**
 * `POST /supervision/bookings/:id/confirm` — the ONE act that turns a booking
 * into compliance evidence.
 *
 * It carries an `occurredAt` and a `durationMinutes` because it is an
 * attestation about a past event, not a rubber stamp on the passage of time:
 * the meeting may have run short, or started late, and what is recorded is what
 * happened rather than what was booked.
 */
export async function confirm(id, input, options) {
    const body = await post(`/supervision/bookings/${id}/confirm`, input, options);
    return body.session;
}
/**
 * `POST /supervision/bookings/:id/close` — a booking that did not become
 * supervision.
 *
 * NOT a delete and NOT a void. The obligation stays open, which is correct: the
 * supervision did not happen. `cancelled` and `no_show` are different facts and
 * an organization looking at a pattern needs to see which.
 */
export async function close(id, input, options) {
    const body = await post(`/supervision/bookings/${id}/close`, input, options);
    return body.session;
}
const COUNTS = {
    booked_ahead: false,
    awaiting_outcome: false,
    unconfirmed: false,
    confirmed_from_booking: true,
    recorded_unbooked: true,
    cancelled: false,
    no_show: false,
    corrected: false,
    voided: false,
};
/**
 * The reading for each kind. Written out rather than composed, because these
 * are the sentences the distinction lives in.
 */
const READING = {
    booked_ahead: {
        label: 'Booked',
        meaning: 'Booked, and still to come. A booking satisfies nothing on its own.',
    },
    awaiting_outcome: {
        label: 'Booked time passed',
        meaning: 'This supervision was booked and the time has passed with nobody saying what happened. ' +
            'It has not been confirmed, and it has not been called off.',
    },
    unconfirmed: {
        label: 'Happened, not confirmed',
        meaning: 'The meeting took place. The supervisor who delivered it has not yet confirmed it, so it ' +
            'counts toward nothing.',
    },
    confirmed_from_booking: {
        label: 'Confirmed',
        meaning: 'The supervisor who delivered this confirmed it happened. It counts.',
    },
    recorded_unbooked: {
        label: 'Recorded',
        meaning: 'Supervision that was recorded directly, without a booking. It counts.',
    },
    cancelled: {
        label: 'Cancelled',
        meaning: 'This booking was called off. The supervision it was for is still owed.',
    },
    no_show: {
        label: 'No-show',
        meaning: 'Nobody came to this booking. The supervision it was for is still owed.',
    },
    corrected: {
        label: 'Corrected',
        meaning: 'A later record replaced this one. The replacement is what counts.',
    },
    voided: {
        label: 'Voided',
        meaning: 'This record was struck out. It counts toward nothing.',
    },
};
function kindOf(row, nowMs) {
    switch (row.status) {
        case 'scheduled': {
            // THE ONE COMPARISON THIS WHOLE CAPABILITY TURNS ON. `scheduledFor` is
            // the booked instant; `occurredAt` mirrors it on a booking because the
            // column is NOT NULL, so either answers, and `scheduledFor` is preferred
            // because it is the one that MEANS "booked for".
            const booked = Date.parse(row.scheduledFor ?? row.occurredAt);
            // An unparseable instant must not become "booked ahead" — that would read
            // a row we cannot place in time as one that is safely in the future.
            if (Number.isNaN(booked))
                return 'awaiting_outcome';
            return booked > nowMs ? 'booked_ahead' : 'awaiting_outcome';
        }
        case 'occurred':
            return 'unconfirmed';
        case 'recorded':
            // A SESSION WITH NO BOOKING IS NOT AN ERROR. Supervision happens
            // unscheduled — a supervisor catches somebody after a difficult visit —
            // and `recordSession` is the door for exactly that. The two kinds are
            // separated so a surface can SAY which it is, never so it can complain.
            return row.scheduledFor === null ? 'recorded_unbooked' : 'confirmed_from_booking';
        case 'cancelled':
            return 'cancelled';
        case 'no_show':
            return 'no_show';
        case 'superseded':
            return 'corrected';
        case 'voided':
            return 'voided';
    }
}
/**
 * Read one supervision row.
 *
 * `now` is required rather than defaulted. A default would let a caller derive
 * a phase without deciding which clock it is against, and every screen that
 * shows more than one row must place them all against the same instant or two
 * bookings a second apart can land on opposite sides of "has the time passed".
 */
export function supervisionMoment(row, now) {
    const kind = kindOf(row, now.getTime());
    const sense = 
    // A booking that was never observed carries a booked instant in
    // `occurred_at` — nobody ever claimed it happened then. `cancelled` and
    // `no_show` are in this set because `closeBooking` leaves `occurred_at`
    // alone; the timestamp on a cancelled row is still only ever a plan.
    kind === 'booked_ahead' ||
        kind === 'awaiting_outcome' ||
        kind === 'cancelled' ||
        kind === 'no_show'
        ? 'booked_for'
        : 'happened_at';
    return {
        kind,
        label: READING[kind].label,
        meaning: READING[kind].meaning,
        instant: sense === 'booked_for' ? (row.scheduledFor ?? row.occurredAt) : row.occurredAt,
        sense,
        counts: COUNTS[kind],
        isIntention: kind === 'booked_ahead',
        needsAnAnswer: kind === 'awaiting_outcome' || kind === 'unconfirmed',
    };
}
/**
 * Is this row a BOOKING at all?
 *
 * `scheduledFor !== null` and nothing else. A row recorded directly has no
 * booked instant, which is what makes `recorded_unbooked` distinguishable from
 * `confirmed_from_booking` at all.
 */
export function isBooking(row) {
    return row.scheduledFor !== null;
}
/**
 * May THIS viewer confirm this booking?
 *
 * NOT A PERMISSION QUESTION, and that is the point. `assertMayConfirm` refuses
 * everybody whose worker row is not `supervisor_worker_id` — organization-wide
 * management included — so a surface that offered Confirm to a holder of
 * `supervision:manage` would be offering an act the server always refuses, on
 * a compliance screen, to the one person whose confirmation would be a
 * fabrication.
 *
 * `viewerWorkerId` is null when the viewer has no worker row, and a null viewer
 * may confirm nothing: `assertMayConfirm` fails closed on the same condition.
 */
export function mayConfirm(row, viewerWorkerId) {
    if (viewerWorkerId === null)
        return false;
    if (row.supervisorWorkerId !== viewerWorkerId)
        return false;
    // The server's own row predicate: `status in ('scheduled','occurred')`.
    return row.status === 'scheduled' || row.status === 'occurred';
}
/**
 * May this viewer CLOSE this booking (cancel it, or record a no-show)?
 *
 * Wider than {@link mayConfirm} on purpose, and the asymmetry is the domain's:
 * closing is not attesting, so a manager cancelling on behalf of a supervisor
 * who is off sick is ordinary and manufactures no evidence. `closeBooking`
 * skips `assertMayConfirm` for an organization-wide manager for exactly that
 * reason.
 */
export function mayClose(row, viewerWorkerId, managesOrgWide) {
    if (row.status !== 'scheduled' && row.status !== 'occurred')
        return false;
    if (managesOrgWide)
        return true;
    return viewerWorkerId !== null && row.supervisorWorkerId === viewerWorkerId;
}
//# sourceMappingURL=supervisionBookings.js.map